<script setup lang="ts">
import { debounce } from "lodash";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import Sidebar from "primevue/sidebar";
import { ref } from "vue";
import { useFilters } from "../composables/useFilters";
import { useSortFilters } from "../composables/useSortFilters";
import { SORT_OPTIONS, SORT_ORDER_OPTIONS } from "../constants";
import { customPricingFiltersPresets } from "../styles.ts";

defineOptions({ name: "CustomPricingFilters" });

const visibleRight = ref<boolean>(false);
const isExpanded = ref<boolean>(false);

const {
  homeInstitutionId,
  updateHomeInstitutionId,
  sessionId,
  updateSessionId,
  titleQ,
  updateTitleQ,
  sessionNameQ,
  updateSessionNameQ,
  overrideId,
  updateOverrideId,
  homeInstitutionNameQ,
  updateHomeInstitutionNameQ,
  updatedByQ,
  updateUpdatedByQ,
  clearFilters,
} = useFilters();

const { field, order, updateField, updateOrder, resetSort } = useSortFilters();

const closeSidebar = (): void => {
  visibleRight.value = false;
};

const clear = (): void => {
  clearFilters();
  resetSort();
  closeSidebar();
};

const updateHomeInstitutionDebounce = debounce(updateHomeInstitutionId, 500);
const updateSessionIdDebounce = debounce(updateSessionId, 500);
const updateTitleQDebounce = debounce(updateTitleQ, 500);
const updateSessionNameQDebounce = debounce(updateSessionNameQ, 500);
const updateOverrideIdDebounce = debounce(updateOverrideId, 500);
const updateHomeInstitutionNameQDebounce = debounce(updateHomeInstitutionNameQ, 500);
const updateUpdatedByQDebounce = debounce(updateUpdatedByQ, 500);
</script>

<template>
  <div class="flex mt-4">
    <Button
      @click="visibleRight = true"
      @mouseover="isExpanded = true"
      @mouseleave="isExpanded = false"
      outlined
      class="!rounded-full !text-cyan-200 !border-cyan-200 h-8 min-w-12 bg-white"
    >
      <div class="flex items-center justify-center">
        <span class="material-symbols-outlined">tune</span>
        <span class="ml-2 leading-8" v-show="isExpanded">Filters</span>
      </div>
    </Button>

    <Sidebar
      v-model:visible="visibleRight"
      position="right"
      :pt="customPricingFiltersPresets.sidebar"
    >
      <template #header>
        <div class="flex justify-between items-center">
          <span class="material-symbols-outlined">tune</span>
          <div class="font-bold uppercase ml-2">Custom Pricing Filters</div>
        </div>
      </template>

      <Accordion
        :activeIndex="0"
        :multiple="true"
        class="shadow-2xl p-6 h-full overflow-y-scroll"
        :pt="customPricingFiltersPresets.accordion"
      >
        <AccordionTab header="Override ID" :pt="customPricingFiltersPresets.accordion">
          <InputText
            :model-value="overrideId ?? ''"
            @update:modelValue="updateOverrideIdDebounce"
            placeholder="Exact override UUID"
            class="w-full mb-4"
          />
        </AccordionTab>

        <AccordionTab header="Session ID" :pt="customPricingFiltersPresets.accordion">
          <InputText
            :model-value="sessionId ?? ''"
            @update:modelValue="updateSessionIdDebounce"
            placeholder="Exact session UUID"
            class="w-full mb-4"
          />
        </AccordionTab>

        <AccordionTab header="Home Institution ID" :pt="customPricingFiltersPresets.accordion">
          <InputText
            :model-value="homeInstitutionId ?? ''"
            @update:modelValue="updateHomeInstitutionDebounce"
            placeholder="Filter by institution ID"
            class="w-full mb-4"
          />
        </AccordionTab>

        <AccordionTab header="Title" :pt="customPricingFiltersPresets.accordion">
          <InputText
            :model-value="titleQ ?? ''"
            @update:modelValue="updateTitleQDebounce"
            placeholder="Substring match on title"
            class="w-full mb-4"
          />
        </AccordionTab>

        <AccordionTab header="Session Name" :pt="customPricingFiltersPresets.accordion">
          <InputText
            :model-value="sessionNameQ ?? ''"
            @update:modelValue="updateSessionNameQDebounce"
            placeholder="Substring match on session name"
            class="w-full mb-4"
          />
        </AccordionTab>

        <AccordionTab header="Home Institution Name" :pt="customPricingFiltersPresets.accordion">
          <InputText
            :model-value="homeInstitutionNameQ ?? ''"
            @update:modelValue="updateHomeInstitutionNameQDebounce"
            placeholder="Substring match on institution name"
            class="w-full mb-4"
          />
        </AccordionTab>

        <AccordionTab header="Updated By" :pt="customPricingFiltersPresets.accordion">
          <InputText
            :model-value="updatedByQ ?? ''"
            @update:modelValue="updateUpdatedByQDebounce"
            placeholder="Substring match on updated_by"
            class="w-full mb-4"
          />
        </AccordionTab>

        <AccordionTab header="Sort By" :pt="customPricingFiltersPresets.accordion">
          <Dropdown
            :model-value="field"
            @update:modelValue="updateField"
            :options="[...SORT_OPTIONS]"
            option-label="name"
            option-value="key"
            class="w-full mb-4"
          />
          <Dropdown
            :model-value="order"
            @update:modelValue="updateOrder"
            :options="[...SORT_ORDER_OPTIONS]"
            option-label="name"
            option-value="key"
            class="w-full mb-4"
          />
        </AccordionTab>
      </Accordion>

      <div class="flex justify-between m-4">
        <Button
          label="Clear"
          severity="secondary"
          class="rounded-none bg-transparent border-0 text-teal-900 p-4"
          @click="clear"
          :pt="customPricingFiltersPresets.button"
        />
        <Button
          label="Close"
          class="rounded-none bg-teal-900 text-white p-4"
          @click="closeSidebar"
          :pt="customPricingFiltersPresets.button"
        />
      </div>
    </Sidebar>
  </div>
</template>

<style scoped>
</style>
